import
  React
from 'react';

import
  PropTypes
from 'prop-types';

import {
  useHistory,
  useLocation
} from 'react-router-dom';

import {
  Image,
  JourneyProvider,
  Text,
  VContainer,
  VFillContainerH
} from '@vodacom/web-common';

import {
  CommonProvider,
  DrupalProvider
} from 'providers';

import {
  PrimaryButton,
  SecondaryButton
} from 'components';

import {
  BrowserNames,
  CacheKeys,
  CustomColors,
  FeatureToggles,
  Icons,
  OriginContexts
} from '../constants';

import {
  AppContext
} from '../context';

import {
  HandleRender,
  withScreenViewTagging
} from 'hocs';

import {
  isEqual,
  isFeatureEnabled,
  openWebView
} from 'helpers';

export const RedirectUI = props => {

  const [ state, setState ] = React.useState({
    countDown: props.countDownTime,
    page: null,
    isError: false,
    isHeartbeatLoading: true,
    isLoading: true
  });

  const history = useHistory();
  const location = useLocation();
  const urlParams = new URLSearchParams(location?.search);
  const isSubscription = urlParams.get('isSubscription') === 'true';
  const isZeroRated = urlParams.get('isZeroRated') === 'true';
  const { device, config } = React.useContext(AppContext);
  const isSafari = device.getBrowser().name.toUpperCase() === BrowserNames.Safari;
  const context = JourneyProvider.get(CacheKeys.ConfigContext, 'value');

  RedirectUI.pageName = isSubscription
    ? 'cbu: connectu: subscription redirect'
    : isZeroRated
      ? 'cbu: connectu: redirect with no data'
      : 'cbu: connectu: redirect with data';

  RedirectUI.tagViewData = {
    page_section: 'leaving connect u',
    content_redirect_name: urlParams.get('targetUrl')
  };

  const redirectToPage = () => {

    if (isEqual(context, OriginContexts.VodaPay)) {
      openWebView(urlParams.get('targetUrl'));
      setTimeout(() => {
        history.goBack();
      }, 500);

      return;
    }

    isSafari
      ? window.location.replace(urlParams.get('targetUrl'))
      : window.location = urlParams.get('targetUrl');
  };

  React.useEffect(() => {

    if (!isSafari && history.length !== 1) {
      window.close();
    }
  });

  React.useEffect(() => {

    const init = async () => {

      const pingSite = async () => {

        const subscriptionId = urlParams.get('id');
        const heartbeat = await CommonProvider.subscriptionLogger(subscriptionId);

        if (!heartbeat.ok || !!heartbeat?.result?.online) {

          setState(prevState => ({
            ...prevState,
            isHeartbeatLoading: false
          }));

          return;
        }

        setState(prevState => ({
          ...prevState,
          isHeartbeatLoading: false,
          isError: true
        }));
      };

      if (!isFeatureEnabled(config, FeatureToggles.EnableSubscriptionLogging)) {

        setState(prevState => ({
          ...prevState,
          isHeartbeatLoading: false
        }));

        return;
      }

      pingSite();
    };

    if (!isSubscription) {

      setState(prevState => ({
        ...prevState,
        isHeartbeatLoading: false
      }));

      return;
    }

    init();
  }, []);

  React.useEffect(() => {

    if (isSubscription || state.isLoading) {
      return;
    }

    const timer = setTimeout(() => {

      const remainingTime = state.countDown - 1;

      if (remainingTime <= 0) {
        redirectToPage();
        return;
      }

      setState(prevState => ({
        ...prevState,
        countDown: remainingTime
      }));
    }, 1000);

    return () => clearTimeout(timer);

  }, [ state.countDown, state.isLoading ]);

  React.useEffect(() => {

    const getPages = async () => {

      const response = await DrupalProvider.getCachedItem(CacheKeys.Pages, null, DrupalProvider.getPages);

      const page = response?.result?.find(page => page?.title === (isSubscription
        ? 'Subscription warning'
        : isZeroRated
        ? 'Redirection zero rated'
        : 'Redirection data charges'
      ));

      setState(prevState => ({
        ...prevState,
        isLoading: false,
        page: page
      }));

    };

    getPages();
  }, []);

  return (

    <HandleRender
      isLoading={state.isLoading || state.isHeartbeatLoading}
      isError={state.isError}
      error={{ onClick: () => {window.close();} }}>

      <VFillContainerH
        width={'100%'}
        maxWidth={'375px'}
        boxSizing={'border-box'}
        padding={'0 28px 48px'}
        margin={'15vh auto 0'}>

        <VContainer
          margin={'0 0 5vh'}>

          <Image
            src={Icons.VodacomLogo}
            width={'80px'}
            height={'80px'}/>

        </VContainer>

        <Text
          color={CustomColors.Black}
          fontSize={'20px'}
          fontWeight={'bold'}>

          {'Leaving connectU'}
        </Text>

        <VContainer
          margin={'16px 0 42px'}>

          <Text
            textAlign={'center'}
            dangerouslySetInnerHTML={
              {
                __html: state.page?.content
              }
            }/>

        </VContainer>

        { !isSubscription &&

          <Text
            color={CustomColors.Grey7}
            fontSize={'16px'}
            textAlign={'center'}>

            {`Redirection in...${state.countDown}`}
          </Text>
        }

        <PrimaryButton
          margin={'auto 0 32px'}
          text={isSubscription ? 'Continue' : 'Go now'}
          onClick={redirectToPage}/>

        <SecondaryButton
          text={'Cancel'}
          onClick={() => {

            if (!isEqual(context, OriginContexts.VodaPay)) {

              window?.close?.();
              return;
            }

            history.goBack();
          }}/>

      </VFillContainerH>
    </HandleRender>
  );
};

RedirectUI.propTypes = {
  countDownTime: PropTypes.number
};

RedirectUI.defaultProps = {
  countDownTime: 5
};

export const Redirect = withScreenViewTagging(RedirectUI);
